import React, { useState } from "react";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { X } from "lucide-react";
import ispaiLogo from "@/assets/ispai-logo-exec.png";

interface ExecutiveSummaryPopupProps {
  triggerElement: React.ReactNode;
}

export const ExecutiveSummaryPopup: React.FC<ExecutiveSummaryPopupProps> = ({
  triggerElement,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <div onClick={() => setIsOpen(true)}>{triggerElement}</div>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0 bg-white">
        <button
          onClick={() => setIsOpen(false)}
          className="absolute right-4 top-4 z-10 p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
        >
          <X className="w-5 h-5 text-gray-600" />
        </button>

        <div className="px-8 py-10 md:px-12">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <img src={ispaiLogo} alt="iSPAI Logo" className="h-20 w-auto" />
          </div>

          {/* Main Title */}
          <h1 className="text-3xl font-bold text-center text-gray-900 mb-10">
            Investor Overview
          </h1>

          {/* Company Overview */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4 border-b border-gray-200 pb-2">
              Company Overview
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                iSPAI ("I Spy") is building a system of record for trusted knowledge designed for the age of AI.
              </p>
              <p>
                As organizations race to deploy AI agents, a critical gap has emerged: data alone is not enough. AI systems require structured, validated context (definitions, relationships, decisions, and domain expertise, etc.) to produce reliable and explainable outcomes. Today, that context is fragmented across systems, buried in tribal knowledge, and lost in workflows.
              </p>
              <p>
                iSPAI solves this by creating a decentralized knowledge network and marketplace that transforms human expertise into structured, validated Knowledge Bases (KBs). These KBs function as living context graphs, capturing not just what is known, but why it is trusted, how it was validated, and when it should be reused.
              </p>
              <p>
                By enabling humans to own, attribute, and monetize their knowledge, iSPAI introduces a new techno-economic model for AI where value flows to the individuals and organizations that contribute to successful outcomes. In this model, humans operate "over-the-loop," with AI acting as their representative rather than their replacement.
              </p>
              <p>
                iSPAI serves enterprises, industry organizations, and government agencies by providing the missing context layer that powers reliable, auditable, and reusable AI systems. We believe the real value in AI lies not in the agent itself, but in the structured knowledge and context it operates on, and we are building the infrastructure to capture, validate, and monetize that layer.
              </p>
              <p>
                Our mission is to define a new role for humans in an AI-driven future by solving the fundamental problem of knowledge entropy, ensuring that critical expertise is preserved, trusted, and continuously compounding over time.
              </p>
            </div>
          </section>

          {/* Investment Highlights */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4 border-b border-gray-200 pb-2">
              Investment Highlights
            </h2>
            <div className="space-y-5 text-gray-700">
              {/* Highlight 1 */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">1. Strong Market Validation</h3>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>CURT (Construction Users Roundtable) selected iSPAI for a sector-wide POC and pilots.</li>
                  <li>Multiple Fortune-scale industries (Construction, Oil & Gas, Government) have signaled strong interest.</li>
                  <li>Validated enterprise need to combat knowledge entropy - billions lost annually due to turnover, fragmentation, and inability to capture expertise.</li>
                </ul>
              </div>

              {/* Highlight 2 */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">2. Government Traction</h3>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>The U.S. GSA rated iSPAI a "green AI partner".</li>
                  <li>GSA requested preparation for a 2026 federal collaboration to support a National Knowledge Network initiative.</li>
                </ul>
              </div>

              {/* Highlight 3 */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">3. Completed MVP & Ready for Early Adopter Deployment</h3>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Full product workflows implemented (for a 2026 launch):
                    <ul className="list-none ml-6 mt-1 space-y-1">
                      <li>○ Creation: Create → Contribute → Confirm</li>
                      <li>○ Consumption: Compose → Consume → Compensate</li>
                      <li>○ Attribution through smart-contract-ready architecture</li>
                    </ul>
                  </li>
                  <li>First revenue expected in 2026 through enterprise and government pilots.</li>
                </ul>
              </div>

              {/* Highlight 4 */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">4. High-Caliber Leadership & Advisors</h3>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Founding team includes experts across AI, Web3, enterprise systems, and knowledge engineering.</li>
                  <li>Advisory board includes two retired Fortune 100 O&G CIOs and CURT leadership.</li>
                  <li>iSPAI's Chief Technical Advisor literally wrote the book on Blockchain for Business.</li>
                </ul>
              </div>

              {/* Highlight 5 */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">5. Massive Market Opportunity</h3>
                <p className="ml-4 mb-2">iSPAI sits at the intersection of four rapidly scaling sectors:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Enterprise AI</li>
                  <li>Decentralized knowledge ownership</li>
                  <li>Workforce transformation</li>
                  <li>Web3 attribution and tokenized IP</li>
                </ul>
                <p className="ml-4 mt-2">This creates a long-term SaaS, transaction-fee, and marketplace revenue model with significant defensibility.</p>
              </div>

              {/* Highlight 6 */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">6. Exceptional Capital Efficiency</h3>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Only $175k raised prior to due diligence.</li>
                  <li>Delivered MVP, enterprise commitments, and government adoption signals on minimal burn.</li>
                  <li>Highly capital-efficient technical and go-to-market execution.</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Why iSPAI Now */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4 border-b border-gray-200 pb-2">
              Why iSPAI Now
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                AI adoption has reached an inflection point, but most deployments are failing. Not because of model limitations, but because AI systems lack the context needed to reason, make decisions, and produce reliable outcomes.
              </p>
              <p>
                Despite a decade of data centralization, critical knowledge remains fragmented across systems, workflows, and human expertise. Without structured context like definitions, relationships, decisions, and validation, AI cannot operate effectively or defensibly. At the same time, organizations are losing billions as experienced workers retire or churn, taking essential tribal knowledge with them.
              </p>
              <p>
                iSPAI solves this by transforming human expertise into structured, validated knowledge that serves as the context layer for AI. By enabling individuals to codify, own, and monetize their contributions, iSPAI turns experiential wisdom into a dynamic, reusable, and economically aligned system of record.
              </p>
              <p>
                This opportunity is driven by converging shifts: AI hitting a context bottleneck, rising demand for explainable and auditable systems, workforce decentralization, and the emergence of real-time, usage-based compensation models.
              </p>
              <p>
                iSPAI is uniquely positioned to lead this transition - building the infrastructure where context is captured, validated, and made economically valuable across organizations and AI systems.
              </p>
            </div>
          </section>

          {/* Conclusion */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4 border-b border-gray-200 pb-2">
              Conclusion
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>Recently completed due diligence confirms iSPAI has:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Real traction,</li>
                <li>A working product,</li>
                <li>Enterprise and federal demand, and</li>
                <li>A differentiated model in one of the fastest-growing sectors of the global economy.</li>
              </ul>
              <p className="font-medium">
                iSPAI is an early-stage, high-leverage opportunity with validated need and strong founder-market fit.
              </p>
            </div>
          </section>

          {/* Contact */}
          <section className="text-center border-t border-gray-200 pt-8">
            <p className="text-gray-700 mb-6">
              For more information or to set up a discussion with iSPAI, please contact Trey Fleming at{" "}
              <a href="mailto:trey.fleming@ispai.io" className="text-blue-600 hover:underline font-medium">
                trey.fleming@ispai.io
              </a>
            </p>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
};
